import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Upload, Sparkles } from 'lucide-react';
import { optimizeImageUrl } from './utils/imageUtils';
import './VisualSearch.css';

// Same behaviour as the standalone Atelier UI, restyled with Indriya classes only.
// All data comes from the Node proxy: /api/visual/* -> FastAPI image-similarity service.
const toVisualSrc = (localSrc) => {
  if (!localSrc) return localSrc;
  if (localSrc.startsWith('/images/')) return `/api/visual/images/${localSrc.slice('/images/'.length)}`;
  return localSrc;
};

const remoteFallback = (url) => optimizeImageUrl(url);

// iPhone photos arrive as HEIC/HEIF, which most browsers can't render or
// upload usefully — convert to JPEG in-browser before preview + search.
const isHeicFile = (f) => /\.hei[cf]$/i.test(f?.name || '') || f?.type === 'image/heic' || f?.type === 'image/heif';

async function api(path, opts) {
  const r = await fetch(path, opts);
  if (!r.ok) throw new Error((await r.text()).slice(0, 200));
  return r.json();
}

const SkeletonCard = () => (
  <div className="product-card skeleton-card">
    <div className="skeleton-image" />
    <div className="skeleton-body">
      <div className="skeleton-line skeleton-title" />
      <div className="skeleton-line skeleton-price" />
      <div className="skeleton-specs">
        <div className="skeleton-line skeleton-spec" />
        <div className="skeleton-line skeleton-spec" />
      </div>
    </div>
  </div>
);

const VisualCard = ({ r, onOpen, onSimilar }) => {
  const [mainIdx, setMainIdx] = useState(0);
  const main = r.images[mainIdx] || r.images[0];
  const mainSrc = toVisualSrc(main.local_src);

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMainIdx((prev) => (prev + 1) % r.images.length);
  };

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMainIdx((prev) => (prev - 1 + r.images.length) % r.images.length);
  };

  const specs = [
    r.sku && { label: 'SKU', value: r.sku },
    r.jewellery_type && { label: 'Type', value: r.jewellery_type },
    r.metal_color && { label: 'Metal', value: r.metal_color },
    r.collection && { label: 'Collection', value: r.collection },
    r.design_theme && { label: 'Theme', value: r.design_theme },
    r.occasion && { label: 'Occasion', value: r.occasion },
  ].filter(Boolean);

  return (
    <article className="product-card">
      <div className="card-image-section">
        {r.category && <span className="category-badge">{r.category}</span>}
        <div className="image-gallery-wrapper">
          <img
            loading="lazy"
            src={mainSrc}
            alt={r.name || 'Jewellery design'}
            style={{ cursor: 'zoom-in' }}
            onClick={() => onOpen(r)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(r); }
            }}
            tabIndex={0}
            role="button"
            aria-label={`Zoom ${r.name || 'design'}`}
            onError={(e) => { e.target.onerror = null; e.target.src = remoteFallback(main.image_url); }}
          />
          {r.images.length > 1 && (
            <>
              <button className="gallery-btn prev-btn" onClick={handlePrevImage} aria-label="Previous image">‹</button>
              <button className="gallery-btn next-btn" onClick={handleNextImage} aria-label="Next image">›</button>
              <div className="gallery-dots">
                {r.images.map((im, i) => (
                  <span key={im.image_id} className={`dot ${i === mainIdx ? 'active' : ''}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="product-details">
        <div className="details-header">
          <h4 title={r.name}>{r.name || '—'}</h4>
          {r.price ? <p className="price">₹{Number(r.price).toLocaleString('en-IN')}</p> : null}
        </div>
        {specs.length > 0 && (
          <div className="product-specs-grid">
            {specs.map((s) => (
              <div className="spec-item" key={s.label}>
                <span className="spec-label">{s.label}</span>
                <span className="spec-value">{s.value}</span>
              </div>
            ))}
          </div>
        )}
        {r.score != null && (
          <div className="visual-score">
            <div className="visual-score-row">
              <span>similarity</span>
              <span className="visual-score-val">{(r.score * 100).toFixed(1)}%</span>
            </div>
            <div className="visual-scorebar" style={{ '--w': `${Math.max(2, r.score * 100)}%` }} />
          </div>
        )}
        <div className="card-footer" style={{ marginTop: '14px' }}>
          <button className="view-link-btn" onClick={() => onSimilar(r.primary_image_id)}>More like this</button>
          {r.product_url
            ? <a href={r.product_url} target="_blank" rel="noreferrer" className="view-link-btn" style={{ textDecoration: 'none' }}>Product ↗</a>
            : <span />}
        </div>
      </div>
    </article>
  );
};

function VisualSearch() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDrag, setIsDrag] = useState(false);
  const [topK, setTopK] = useState(24);
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('browse');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [title, setTitle] = useState('Catalog');
  const [status, setStatus] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isBrowsing, setIsBrowsing] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { product, imgIdx }
  const fileRef = useRef(null);
  const queryTimer = useRef(null);
  const mainRef = useRef(null);
  const perPage = 24;

  const scrollMainTop = () => {
    if (mainRef.current) mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadBrowse = useCallback(async (opts = {}) => {
    const p = opts.page ?? page;
    const q = opts.query !== undefined ? opts.query : query;
    setIsBrowsing(true);
    setStatus('Loading catalog …');
    try {
      const params = new URLSearchParams({
        page: String(p), per_page: String(perPage), q: q.trim(),
        category: 'All', metal_color: 'All', jewellery_type: 'All',
      });
      const d = await api(`/api/visual/browse?${params}`);
      setMode('browse');
      setResults(d.results || []);
      setTotal(d.total || 0);
      setTitle(q.trim() ? `Results for \u201c${q.trim()}\u201d` : 'Catalog');
      setStatus('');
    } catch (e) {
      setStatus('Backend not ready — start the image-similarity service first.');
    } finally {
      setIsBrowsing(false);
    }
  }, [page, query]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api('/api/visual/stats');
        setStats(s);
      } catch (e) {
        setStatus('Backend not ready — start the image-similarity service first.');
      }
      loadBrowse({ page: 0 });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (queryTimer.current) clearTimeout(queryTimer.current);
  }, [previewUrl]);

  const setChosenFile = async (f) => {
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    try {
      let upload = f;
      if (isHeicFile(f)) {
        setIsConverting(true);
        setStatus('Converting HEIC photo…');
        const { default: heic2any } = await import('heic2any');
        const converted = await heic2any({ blob: f, toType: 'image/jpeg', quality: 0.92 });
        const jpeg = Array.isArray(converted) ? converted[0] : converted;
        upload = new File([jpeg], (f.name || 'photo').replace(/\.hei[cf]$/i, '') + '.jpg', { type: 'image/jpeg' });
      }
      setFile(upload);
      setPreviewUrl(URL.createObjectURL(upload));
      setStatus(`${f.name} ready — hit Search similar.`);
    } catch (e) {
      console.error('HEIC conversion failed:', e);
      setStatus('Could not read that photo — please use JPG, PNG, WEBP, or HEIC from Photos.');
    } finally {
      setIsConverting(false);
    }
  };

  const doSearch = async () => {
    if (!file) { setStatus('Choose an image first — or browse below.'); loadBrowse({ page: 0 }); return; }
    setIsSearching(true);
    setStatus('Embedding query & searching …');
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('top_k', String(topK));
      fd.append('category', 'All');
      fd.append('metal_color', 'All');
      fd.append('jewellery_type', 'All');
      const d = await api('/api/visual/search', { method: 'POST', body: fd });
      setMode('results');
      setResults(d.results || []);
      setTitle('Similar designs');
      setStatus(d.results?.length ? `Top match ${(d.results[0].score * 100).toFixed(1)}% similar` : 'No matches — broaden filters.');
      scrollMainTop();
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  const moreLike = async (imageId) => {
    setStatus(`Finding more like ${imageId} …`);
    try {
      const d = await api(`/api/visual/similar/${encodeURIComponent(imageId)}?top_k=${topK}`);
      setMode('results');
      setResults(d.results || []);
      setTitle('More like this');
      setStatus(`${(d.results || []).length} matches`);
      scrollMainTop();
    } catch (e) {
      setStatus(`Error: ${e.message}`);
    }
  };

  const handleClear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = '';
    setPage(0);
    loadBrowse({ page: 0 });
  };

  const handleQueryChange = (v) => {
    setQuery(v);
    if (queryTimer.current) clearTimeout(queryTimer.current);
    queryTimer.current = setTimeout(() => {
      setPage(0);
      loadBrowse({ page: 0, query: v });
    }, 350);
  };

  const openLightbox = (product) => setLightbox({ product, imgIdx: 0 });
  const closeLightbox = () => setLightbox(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const lb = lightbox?.product || null;
  const lbImg = lb ? (lb.images[lightbox.imgIdx] || lb.images[0]) : null;

  return (
    <div className="visual-container">
      <div className="visual-sidebar">
        <div className="visual-sidebar-header">
          <h3>Visual Design Search</h3>
          <p className="visual-sidebar-sub">Upload a reference image — find the closest designs in your catalog</p>
          {stats && (
            <div className="visual-stats-row">
              <span className="dashboard-stat-badge">{Number(stats.images).toLocaleString()} images</span>
              <span className="dashboard-stat-badge">{Number(stats.products).toLocaleString()} products</span>
            </div>
          )}

          <div
            className={`visual-dropzone ${isDrag ? 'drag' : ''} ${file ? 'has-file' : ''}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
            onDragEnter={(e) => { e.preventDefault(); setIsDrag(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDrag(false); }}
            onDrop={(e) => { e.preventDefault(); setIsDrag(false); const f = e.dataTransfer.files?.[0]; if (f) setChosenFile(f); }}
            role="button"
            tabIndex={0}
            aria-label="Upload reference image"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.heic,.heif"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.[0]) setChosenFile(e.target.files[0]); }}
            />
            {!previewUrl ? (
              <>
                <div style={{ color: 'var(--primary-gold)', marginBottom: '8px' }}><Upload size={26} /></div>
                <div style={{ fontSize: 'var(--text-base)', fontWeight: 600 }}>Drag &amp; drop or <span style={{ color: 'var(--primary-wine)', textDecoration: 'underline' }}>browse</span></div>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-light)', marginTop: '4px' }}>JPG / PNG / WEBP / HEIC</div>
              </>
            ) : (
              <img src={previewUrl} className="visual-preview" alt="reference preview" />
            )}
          </div>

          <div className="visual-field">
            <div className="visual-field-label"><label htmlFor="visual-topk">Results</label><output>{topK}</output></div>
            <input id="visual-topk" type="range" min="4" max="100" value={topK} onChange={(e) => setTopK(Number(e.target.value))} className="visual-range" />
          </div>

          <div className="visual-actions">
            <button className="visual-search-btn" onClick={doSearch} disabled={isSearching || isConverting}>
              {isSearching ? 'Searching…' : isConverting ? 'Converting…' : 'Search similar'}
            </button>
            <button className="visual-clear-btn" onClick={handleClear}>Clear</button>
          </div>
          <p className="visual-status">{status}</p>

          <div className="visual-divider">
            <h4>Browse catalog</h4>
            <div className="visual-search-wrap">
              <Search size={16} className="visual-search-icon" />
              <input
                type="text"
                className="visual-input"
                placeholder="Search name or SKU…"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="visual-main" ref={mainRef}>
        <div className="visual-results-head">
          <h2>{title}</h2>
          <span className="visual-count">
            {mode === 'browse'
              ? (total ? `${total.toLocaleString()} designs · page ${page + 1}` : '')
              : (results.length ? `${results.length} designs` : '')}
          </span>
          {mode === 'browse' && (
            <div className="visual-pager">
              <button
                className="visual-pager-btn"
                disabled={page === 0 || isBrowsing}
                onClick={() => { const p = page - 1; setPage(p); loadBrowse({ page: p }); scrollMainTop(); }}
              >
                ← Prev
              </button>
              <button
                className="visual-pager-btn"
                disabled={isBrowsing || results.length < perPage}
                onClick={() => { const p = page + 1; setPage(p); loadBrowse({ page: p }); scrollMainTop(); }}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {(isSearching || isBrowsing) && results.length === 0 ? (
          <div className="product-gallery">
            {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
          </div>
        ) : results.length > 0 ? (
          <div className="product-gallery">
            {results.map((r) => (
              <VisualCard key={r.product_id} r={r} onOpen={openLightbox} onSimilar={moreLike} />
            ))}
          </div>
        ) : (
          <div className="analysis-empty-state">
            <div className="glow-icon"><Sparkles size={32} /></div>
            <h3>Discover by Design</h3>
            <p>Upload a reference image and hit <strong>Search similar</strong>.</p>
          </div>
        )}
      </div>

      {lb && lbImg && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') closeLightbox(); }}>
          <div className="modal-content visual-lightbox" style={{ padding: 0, overflow: 'hidden', textAlign: 'left', maxWidth: '900px', width: '92vw' }}>
            <div className="visual-lb-media">
              <img
                src={toVisualSrc(lbImg.local_src)}
                onError={(e) => { e.target.onerror = null; e.target.src = remoteFallback(lbImg.image_url); }}
                alt={lb.name || ''}
              />
              {lb.images.length > 1 && (
                <div className="visual-thumbs" style={{ justifyContent: 'center' }}>
                  {lb.images.map((im, k) => (
                    <button
                      key={im.image_id}
                      className={`visual-thumb ${k === lightbox.imgIdx ? 'active' : ''}`}
                      onClick={() => setLightbox({ product: lb, imgIdx: k })}
                      title={`View ${k + 1}`}
                    >
                      <img
                        src={toVisualSrc(im.local_src)}
                        onError={(e) => { e.target.onerror = null; e.target.src = remoteFallback(im.image_url); }}
                        alt={`view ${k + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="visual-lb-body" style={{ position: 'relative' }}>
              <button className="lightbox-close-btn" onClick={closeLightbox} aria-label="Close">×</button>
              <h3>{lb.name || '—'}</h3>
              <p className="visual-lb-sku">{lb.sku || ''}{lb.view_count > 1 ? ` · ${lb.view_count} views` : ''}</p>
              {lb.price ? <p className="price">₹{Number(lb.price).toLocaleString('en-IN')}</p> : null}
              <div className="visual-lb-meta">
                {[['Category', lb.category], ['Type', lb.jewellery_type], ['Product', lb.product_type],
                  ['Metal', lb.metal_color], ['Collection', lb.collection], ['Theme', lb.design_theme],
                  ['Occasion', lb.occasion],
                  ['Score', lb.score != null ? `${(lb.score * 100).toFixed(2)}%` : null]]
                  .filter(([, v]) => v)
                  .map(([k, v]) => <p key={k} style={{ margin: 0 }}><span>{k}:</span> {v}</p>)}
              </div>
              <div className="visual-lb-actions">
                <button className="visual-search-btn" style={{ padding: '10px 18px' }} onClick={() => { const pid = lb.primary_image_id; closeLightbox(); moreLike(pid); }}>
                  More like this
                </button>
                {lb.product_url && <a href={lb.product_url} target="_blank" rel="noreferrer" className="visual-clear-btn" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Product ↗</a>}
                <button className="visual-clear-btn" onClick={closeLightbox}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisualSearch;
